import { createReducer } from '@reduxjs/toolkit';
import {
    listVehiclesRequest,
    listVehiclesSuccess,
    listVehiclesFailure,
    listVehiclesDeliveryRequest,
    listVehiclesDeliverySuccess,
    listVehiclesDeliveryFailure,
    setVehicleRequest,
    getVehicleRequest,
    getVehicleSuccess,
    getVehicleFailure,
    updateVehicleRequest,
    updateVehicleSuccess,
    updateVehicleFailure,
    resetErrorRequest,
    getMediaTypesRequest,
    getMediaTypesSuccess,
    getMediaTypesFailure,
    uploadMediaRequest,
    uploadMediaSuccess,
    uploadMediaFailure,
    uploadDocumentRequest,
    uploadDocumentSuccess,
    uploadDocumentFailure,
    getInspectionsRequest,
    getInspectionsSuccess,
    getInspectionsFailure,
    InspectionSection,
    MediaType,
    Vehicle,
    Store,
    getStoreLocationsRequest,
    getStoreLocationsSuccess,
    getStoreLocationsFailure,
    getMakesRequest,
    getMakesSuccess,
    getMakesFailure,
    Make,
    Body,
    updateMediaRequest,
    updateMediaSuccess,
    updateMediaFailure,
    getBodiesRequest,
    getBodiesSuccess,
    getBodiesFailure,
    uploadBeforeAfterMediaRequest,
    uploadBeforeAfterMediaSuccess,
    uploadBeforeAfterMediaFailure,
    genBookletRequest,
    getBookletCarfaxRequest,
    genBookletFactoryBaseConfigurationRequest,
    genBookletWhatWeDidRequest,
    genBookletNotesRequest,
    genBookletWhatHappenedRequest,
} from './actions';

export interface VehicleState {
    [key: string]: any;
    mediatypes: MediaType[];
    inspections: InspectionSection[];
    vehicles_featured: Vehicle[];
    vehicles: Vehicle[];
    vehicles_inventory: Vehicle[];
    vehicle: Vehicle | null;
    deliveries: Vehicle[];
    purchase_inventory: Vehicle[];
    loading: boolean;
    error: string | null;
    stores: Store[];
    bodies: Body[] | null;
    bodies_all: Body[] | null;
    makes_all: Make[] | null;
    makes_active: Make[] | null;
}

const initialState: VehicleState = {
    mediatypes: [],
    deliveries: [],
    purchase_inventory: [],
    inspections: [],
    vehicles: [],
    vehicles_featured: [],
    vehicles_inventory: [],
    vehicle: null,
    stores: [],
    bodies: [],
    bodies_all: [],
    makes_all: [],
    makes_active: [],
    loading: false,
    error: null,
};

const vehicleReducer = createReducer(initialState, (builder) => {
    builder
        .addCase(resetErrorRequest, (state) => {
            state.error = null;
        })
        .addCase(listVehiclesRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(listVehiclesSuccess, (state, action) => {
            state[action.payload.save_as || 'vehicles'] = action.payload.vehicles;
            state.loading = false;
        })
        .addCase(listVehiclesFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(setVehicleRequest, (state, action) => {
            state.vehicle = action.payload;
            state.error = null;
        })
        .addCase(getVehicleRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getVehicleSuccess, (state, action) => {
            state.vehicle = action.payload; // Set vehicles from the server
            state.loading = false;
        })
        .addCase(getVehicleFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(getMediaTypesRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getMediaTypesSuccess, (state, action) => {
            state.loading = false;
            state.mediatypes = action.payload;
        })
        .addCase(getMediaTypesFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(updateVehicleRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(updateVehicleSuccess, (state, action) => {
            state.vehicles = state.vehicles.map((el) => {
                if (el.id == action.payload.id) {
                    return {
                        ...el,
                        ...action.payload,
                    }
                }
                return el;
            });
            state.deliveries = state.deliveries.filter((el) => el.id != action.payload.id);
            if (state.vehicle?.id == action.payload.id) {
                state.vehicle = {
                    ...state.vehicle,
                    ...action.payload,
                };
            }
            state.loading = false;
        })
        .addCase(updateVehicleFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(listVehiclesDeliveryRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(listVehiclesDeliverySuccess, (state, action) => {
            state.loading = false;
            state.deliveries = action.payload;
        })
        .addCase(listVehiclesDeliveryFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(uploadMediaRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(uploadMediaSuccess, (state, action) => {
            state.loading = false;
            state.vehicles = state.vehicles.map((el) => {
                if (el.id == action.payload.vehicle_id) {
                    return {
                        ...el,
                        ...action.payload,
                    }
                }
                return el;
            });
            if (state.vehicle?.id == action.payload.vehicle_id) {
                state.vehicle = {
                    ...state.vehicle,
                    medias: [...(state.vehicle.medias || []), ...action.payload.medias],
                };
            }
        })
        .addCase(uploadMediaFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(uploadDocumentRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(uploadDocumentSuccess, (state, action) => {
            state.loading = false;
            if (state.vehicle?.id == action.payload.vehicle_id) {
                state.vehicle = {
                    ...state.vehicle,
                    documents: [...(state.vehicle.documents || []), ...action.payload.documents],
                };
            }
            state.vehicles = state.vehicles.map((el) => {
                if (el.id == action.payload.vehicle_id) {
                    return {
                        ...el,
                        ...action.payload,
                    }
                }
                return el;
            });
        })
        .addCase(uploadDocumentFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(getInspectionsRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getInspectionsSuccess, (state, action) => {
            state.loading = false;
            state.inspections = action.payload;
        })
        .addCase(getInspectionsFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(getStoreLocationsRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getStoreLocationsSuccess, (state, action) => {
            state.loading = false;
            state.stores = action.payload;
        })
        .addCase(getStoreLocationsFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(getMakesRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getMakesSuccess, (state, action) => {
            state.loading = false;
            state.makes_all = action.payload.all;
            state.makes_active = action.payload.active;

            if (action.payload.active.length > 0) {
                const uniqueBodies = new Map();

                action.payload.active.forEach((make: Make) => {
                    (make.bodies || []).forEach((body: Body) => {
                        uniqueBodies.set(body.id, body);
                    });
                });
                state.bodies = Array.from(uniqueBodies.values());
            }
        })
        .addCase(getMakesFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(updateMediaRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(updateMediaSuccess, (state, action) => {
            state.loading = false;
            state.vehicles = state.vehicles.map((el) => {
                if (el.id == action.payload.vehicle_id) {
                    return {
                        ...el,
                        medias: el.medias?.map((media) => {
                            if (media.id == action.payload.media.id) {
                                return {
                                    ...media,
                                    ...action.payload.media,
                                };
                            }
                            return media;
                        }),
                    };
                }
                return el;
            });
            if (state.vehicle?.id == action.payload.vehicle_id) {
                state.vehicle = {
                    ...state.vehicle,
                    medias: state.vehicle.medias?.map((media) => {
                        if (media.id == action.payload.media.id) {
                            return {
                                ...media,
                                ...action.payload.media,
                            };
                        }
                        return media;
                    }),
                };
            }
        })
        .addCase(updateMediaFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(getBodiesRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getBodiesSuccess, (state, action) => {
            state.loading = false;
            state.bodies_all = action.payload;
        })
        .addCase(getBodiesFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(uploadBeforeAfterMediaRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(uploadBeforeAfterMediaSuccess, (state, action) => {
            state.loading = false;
            state.vehicles = state.vehicles.map((el) => {
                if (el.id == action.payload.vehicle_id) {
                    return {
                        ...el,
                        before_after: [
                            ...(el.before_after || []),
                             ...[action.payload.before_after]
                        ],
                    }
                }
                return el;
            });
            if (state.vehicle?.id == action.payload.vehicle_id) {
                state.vehicle = {
                    ...state.vehicle,
                    before_after: [
                        ...(state.vehicle.before_after || []),
                        ...[action.payload.before_after]]
                    ,
                };
            }
        })
        .addCase(uploadBeforeAfterMediaFailure, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(genBookletRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getBookletCarfaxRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(genBookletFactoryBaseConfigurationRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(genBookletWhatWeDidRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(genBookletNotesRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(genBookletWhatHappenedRequest, (state) => {
            state.loading = true;
            state.error = null;
        })
});

export default vehicleReducer;
